import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, CartErrorEvent } from '@theme/events';

/**
 * A custom element that adds the gift wrap product to the cart when its checkbox is checked.
 * The offer hides itself as soon as the product has been added successfully.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} checkbox - The gift wrap checkbox.
 *
 * @extends {Component<Refs>}
 */
class CartGiftWrapComponent extends Component {
  requiredRefs = ['checkbox'];

  add = async () => {
    const { checkbox } = this.refs;
    const { variantId, sectionId } = this.dataset;

    if (!checkbox.checked || !variantId) return;

    // Optimistically hide the offer right away so it can't be triggered twice
    // and the customer gets instant feedback that the bag was added.
    checkbox.disabled = true;
    this.hidden = true;

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({ id: variantId, quantity: 1, sections: sectionId ? [sectionId] : undefined }),
        })
      );

      const data = await response.json();

      if (data.status) {
        throw new Error(data.message || 'Cart add error');
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          source: 'cart-gift-wrap-component',
          itemCount: cart.item_count,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);

      // Something went wrong: bring the offer back so the customer can try again.
      this.hidden = false;
      checkbox.checked = false;
      checkbox.disabled = false;

      const message = error instanceof Error ? error.message : String(error);
      this.dispatchEvent(new CartErrorEvent(this.id, message, {}, {}));
    }
  };
}

if (!customElements.get('cart-gift-wrap-component')) {
  customElements.define('cart-gift-wrap-component', CartGiftWrapComponent);
}
